import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

const STYLES = {
    dark: {
        input: "border-blush/40 text-blush placeholder-blush/40 focus:border-tulip",
        btn: "bg-rose text-blush hover:bg-honeysuckle hover:text-garden",
        text: "text-blush/70",
        link: "text-tulip hover:text-porcelain",
        panel: "border-tulip/50",
    },
    light: {
        input: "border-garden/30 text-garden placeholder-garden/40 focus:border-rose",
        btn: "bg-rose text-blush hover:bg-honeysuckle hover:text-garden",
        text: "text-garden/70",
        link: "text-rose hover:text-garden",
        panel: "border-rose/50",
    },
    rose: {
        input: "border-blush/40 text-blush placeholder-blush/50 focus:border-blush",
        btn: "bg-honeysuckle text-garden hover:bg-blush hover:text-garden",
        text: "text-blush/85",
        link: "text-blush underline decoration-blush/50 hover:decoration-blush",
        panel: "border-blush/60",
    },
};

export default function EmailCapture({ id = "hero", variant = "dark" }) {
    const s = STYLES[variant] || STYLES.dark;
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState("");
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const value = email.trim().toLowerCase();
        if (!EMAIL_RE.test(value)) {
            toast.error("That email doesn’t look right — mind checking it?");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API}/api/newsletter`, { email: value, consent: true });
            setToken(res?.data?.download_token || "");
            setDone(true);
            toast.success("You’re on the list. Talk soon.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div data-testid={`${id}-confirm-message`} className={`border ${s.panel} p-5 md:p-6`}>
                <p className="font-display font-bold text-xl md:text-2xl">You’re on the list.</p>
                <p className={`text-sm mt-1.5 ${s.text}`}>The full song link is on its way to your inbox.</p>
                {token && (
                    <a
                        href={`${API}/api/download/full-song?token=${token}`}
                        data-testid={`${id}-download-btn`}
                        className={`mt-4 inline-flex items-center gap-2 ${s.btn} px-5 py-3 min-h-[48px] text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors duration-300`}
                    >
                        <Download className="h-4 w-4" aria-hidden="true" />
                        Download the full song ↓
                    </a>
                )}
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="w-full" noValidate>
            <div className="flex flex-col sm:flex-row gap-3">
                <label htmlFor={`${id}-email`} className="sr-only">Email address</label>
                <input
                    id={`${id}-email`}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={`${id}-email-input`}
                    className={`flex-1 bg-transparent border px-5 py-4 min-h-[52px] ${s.input} transition-colors duration-200 focus:outline-none`}
                />
                <button
                    type="submit"
                    disabled={loading}
                    data-testid={`${id}-email-submit`}
                    className={`min-h-[52px] px-7 text-xs md:text-sm uppercase tracking-[0.18em] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${s.btn}`}
                >
                    {loading ? "Sending…" : "Get the full song"}
                </button>
            </div>
            <p className={`mt-3 text-[11px] ${s.text}`}>
                Free. By continuing you agree to the{" "}
                <a href="/privacy" data-testid={`${id}-privacy-link`} className={`${s.link} underline underline-offset-2`}>
                    Privacy Policy
                </a>
                . Unsubscribe anytime.
            </p>
        </form>
    );
}
